import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Send, 
  AlertTriangle, 
  Clock, 
  User, 
  CheckCircle, 
  XCircle,
  Phone,
  Building2,
  ExternalLink,
  ArrowRight,
  MessageCircle,
  Shield,
  Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import { escalateTicketToExternal, getTicketEscalationHistory, getSystems } from '../../../../models/helpdeskModel';
import { useAuth } from '../../../../context/AuthContext';
import Button from '../../../../components/common/Button';
import WysiwygEditor from '../../../../components/common/WysiwygEditor';

const EscalationTab = ({ ticket, onTicketUpdate }) => {
  const { isIT } = useAuth();
  const [systems, setSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [escalationHistory, setEscalationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  
  // Form data for escalation
  const [escalationForm, setEscalationForm] = useState({
    system_id: '',
    contact_email: '',
    subject: '',
    message: '',
    cc: '',
    priority: 'normal'
  });

  useEffect(() => {
    if (ticket) {
      fetchSystems();
      fetchEscalationHistory();
      
      // Extract system_id from multiple possible locations
      const systemId = ticket.system_id || 
                      ticket.custom_fields?.system_id || 
                      ticket.asset_id || 
                      '';
      
      // Pre-populate subject with ticket number for proper email threading
      const ticketNumber = ticket.ticket_number || ticket.number || `#${ticket.id || ticket.ticket_id}`;
      const ticketSubject = ticket.subject || ticket.ticket_subject || ticket.title || 'Support Request';
      
      setEscalationForm(prev => ({
        ...prev,
        subject: `${ticketNumber} - ${ticketSubject}`,
        system_id: systemId
      }));
    }
  }, [ticket]);

  // Auto-select ticket's system if it has external support
  useEffect(() => {
    if (ticket && systems.length > 0) {
      const ticketSystemId = ticket.system_id || 
                            ticket.custom_fields?.system_id || 
                            ticket.asset_id;
                            
      if (ticketSystemId) {
        const ticketSystem = systems.find(s => 
          s.asset_id == ticketSystemId
        );
        
        if (ticketSystem) {
          handleSystemSelect(ticketSystem.asset_id);
        }
      }
    }
  }, [ticket, systems]); // Run when ticket or systems change

  // Helper function to parse concatenated email addresses
  const parseEmailAddresses = (emailString) => {
    if (!emailString || typeof emailString !== 'string') {
      return { primary: '', cc: '' };
    }

    // Use regex to extract email addresses from the concatenated string
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = emailString.match(emailRegex) || [];
    
    if (emails.length === 0) {
      return { primary: '', cc: '' };
    }
    
    // First email goes to "To", rest go to "CC"
    const primary = emails[0];
    const cc = emails.length > 1 ? emails.slice(1).join(', ') : '';
    
    return { primary, cc };
  };

  const fetchSystems = async () => {
    try {
      const response = await getSystems();

      // Handle the nested data structure from assets API
      let systemsData = [];
      if (response.status === 1) {
        if (response.data?.data?.rows) {
          // New nested structure: data.data.rows
          systemsData = response.data.data.rows;
        } else if (response.data) {
          // Fallback: direct data array
          systemsData = response.data;
        } else if (response.systems) {
          // Legacy: systems property
          systemsData = response.systems;
        }
      }

      // Filter systems that have external support contacts
      const systemsWithSupport = systemsData.filter(system => {
        return system.asset_support && 
          system.asset_support.trim() !== '' &&
          system.asset_support.includes('@');
      });

      setSystems(systemsWithSupport);
      
    } catch (error) {
      console.error('Error fetching systems:', error);
      toast.error('Failed to load external support systems');
    }
  };

  const fetchEscalationHistory = async () => {
    try {
      setLoading(true);
      const response = await getTicketEscalationHistory(ticket.id || ticket.ticket_id);
      if (response.status === 1 && Array.isArray(response.escalations)) {
        setEscalationHistory(response.escalations);
      }
    } catch (error) {
      console.error('Error fetching escalation history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSelect = (systemId) => {
    setSelectedSystem(systemId);
    
    if (systemId) {
      // Find the selected system
      const system = systems.find(s => s.asset_id == systemId);
      
      if (system) {
        // Parse the concatenated email addresses
        const { primary, cc } = parseEmailAddresses(system.asset_support);
        
        setEscalationForm(prev => ({
          ...prev,
          system_id: systemId,
          contact_email: primary,
          cc: cc
        }));
      } else {
        // System not found, clear contact fields but keep system_id
        setEscalationForm(prev => ({
          ...prev,
          system_id: systemId,
          contact_email: '',
          cc: ''
        }));
      }
    } else {
      // No system selected, clear all system-related fields
      setEscalationForm(prev => ({
        ...prev,
        system_id: '',
        contact_email: '',
        cc: ''
      }));
    }
  };

  const handleEscalate = async () => {
    if (!escalationForm.system_id || !escalationForm.contact_email || !escalationForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setEscalating(true);
      const response = await escalateTicketToExternal(ticket.id || ticket.ticket_id, {
        system_id: escalationForm.system_id,
        contact_email: escalationForm.contact_email,
        subject: escalationForm.subject,
        message: escalationForm.message,
        cc: escalationForm.cc,
        priority: escalationForm.priority
      });

      if (response.status === 1) {
        toast.success('Ticket escalated to external support successfully');
        setShowEscalationForm(false);
        // Reset form with proper ticket number format for future escalations
        const ticketNumber = ticket.ticket_number || ticket.number || `#${ticket.id || ticket.ticket_id}`;
        const ticketSubject = ticket.subject || ticket.ticket_subject || ticket.title || 'Support Request';
        
        setEscalationForm({
          system_id: '',
          contact_email: '',
          subject: `${ticketNumber} - ${ticketSubject}`,
          message: '',
          cc: '',
          priority: 'normal'
        });
        fetchEscalationHistory();
        if (onTicketUpdate) {
          onTicketUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to escalate ticket');
      }
    } catch (error) {
      console.error('Error escalating ticket:', error);
      toast.error('Failed to escalate ticket to external support');
    } finally {
      setEscalating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Send className="h-4 w-4 text-blue-500" />;
      case 'replied':
        return <MessageCircle className="h-4 w-4 text-green-500" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Sent' },
      replied: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Replied' },
      resolved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Resolved' },
      failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Failed' },
      pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Pending' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        {getStatusIcon(status)}
        <span className="ml-1">{config.label}</span>
      </span>
    );
  };

  if (!isIT()) {
    return (
      <div className="text-center py-8">
        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">External Escalation</h3>
        <p className="text-gray-500">
          Only IT staff can escalate tickets to external support providers.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <ExternalLink className="h-5 w-5 text-orange-600" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">External Support Escalation</h3>
            <p className="text-sm text-gray-500">
              Escalate this ticket to external support providers for specialized assistance
            </p>
          </div>
        </div>
        
        {!showEscalationForm && (
          <Button
            onClick={() => setShowEscalationForm(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            leftIcon={<Zap className="h-4 w-4" />}
          >
            Escalate Ticket
          </Button>
        )}
      </div>

      {/* Escalation Form */}
      {showEscalationForm && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Escalate to External Support</h4>
            <Button
              onClick={() => setShowEscalationForm(false)}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
          </div>

          <div className="space-y-4">
            {/* System Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Support System *
              </label>
              <select
                value={escalationForm.system_id}
                onChange={(e) => handleSystemSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
                required
              >
                <option value="">Select a system with external support...</option>
                {systems.map((system) => (
                  <option key={system.asset_id} value={system.asset_id}>
                    {system.asset_name}
                  </option>
                ))}
              </select>
              {systems.length === 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  No systems with external support contacts found. Configure external support contacts in the Assets module.
                </p>
              )}
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email *
              </label>
              <input
                type="email"
                value={escalationForm.contact_email}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-400"
                placeholder="external.support@provider.com"
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={escalationForm.subject}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-400"
                placeholder="Enter escalation subject"
                required
              />
            </div>

            {/* CC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC (Optional)
              </label>
              <input
                type="text"
                value={escalationForm.cc}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, cc: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-400"
                placeholder="cc@example.com, another@example.com"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={escalationForm.priority}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalation Message *
              </label>
              <div className="border border-gray-300 rounded-lg">
                <WysiwygEditor
                  value={escalationForm.message}
                  onChange={(value) => setEscalationForm(prev => ({ ...prev, message: value }))}
                  placeholder="Describe the issue and provide relevant details for external support..."
                  minHeight="120px"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Include original ticket details, troubleshooting steps attempted, and specific assistance needed.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowEscalationForm(false)}
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEscalate}
                loading={escalating}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                leftIcon={<Send className="h-4 w-4" />}
              >
                {escalating ? 'Escalating...' : 'Send Escalation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Escalation History */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Escalation History</h4>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading escalation history...</p>
          </div>
        ) : escalationHistory.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <ExternalLink className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <h5 className="text-sm font-medium text-gray-900 mb-1">No External Escalations</h5>
            <p className="text-sm text-gray-500">
              This ticket has not been escalated to external support yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {escalationHistory.map((escalation, index) => (
              <div
                key={escalation.id || index}
                className="bg-white rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {escalation.system_name || 'External System'}
                      </span>
                      {getStatusBadge(escalation.status)}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Mail className="h-3 w-3" />
                        <span>{escalation.contact_email}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>
                          {new Date(escalation.created_at).toLocaleDateString()} at {new Date(escalation.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{escalation.escalated_by_name}</span>
                      </div>
                    </div>

                    <h6 className="font-medium text-gray-900 mb-2">{escalation.subject}</h6>
                    
                    {escalation.message && (
                      <div 
                        className="text-sm text-gray-600 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: escalation.message }}
                      />
                    )}

                    {escalation.response && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">External Support Response</span>
                          {escalation.response_at && (
                            <span className="text-xs text-green-600">
                              {new Date(escalation.response_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div 
                          className="text-sm text-green-700 prose max-w-none"
                          dangerouslySetInnerHTML={{ __html: escalation.response }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h6 className="font-medium text-blue-900 mb-1">External Escalation Process</h6>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• Escalations are sent as emails to external support providers</p>
              <p>• Responses are automatically imported and linked to this ticket</p>
              <p>• The ticket status will be updated to "Escalated" upon successful escalation</p>
              <p>• You'll be notified when external support responds to the escalation</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscalationTab;